import { useState, useMemo, useEffect, useRef } from "react";
import type { SlotAvailability, ResourceType } from "../lib/types";
import { RESOURCE_LABELS, DAY_NAMES } from "../lib/types";
import { toLocalDateStr } from "../lib/hooks";
import { placeWorkshops } from "../lib/workshopGrid";
import { BookingPopup } from "./BookingPopup";
import { CancelBookingPopup } from "./CancelBookingPopup";
import { AdminSlotPopup } from "./AdminSlotPopup";

interface CalendarProps {
  slots: SlotAvailability[];
  weekStart: Date;
  onWeekChange: (d: Date) => void;
  isLoggedIn: boolean;
  isAdmin?: boolean;
  isAdminManageMode?: boolean;
  myBookedSlotIds?: Set<number>;
  myBookingGroupMap?: Map<number, string>;
  creditBalance?: number;
  onRefetch: () => void;
  onCancelled?: () => void;
  onLoginPrompt: () => void;
}

type CalendarView = "week" | "day";

function getMonday(d: Date): Date {
  const date = new Date(d);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function formatDateShort(d: Date): string {
  return d.toLocaleDateString("sk-SK", { day: "numeric", month: "numeric" });
}

function cellColor(slot: SlotAvailability, isAdminView: boolean): string {
  if (isAdminView && slot.is_active === false) return "cell-blocked";
  if (slot.resource_type === "workshop") return "cell-workshop";
  if (slot.available === 0) return "cell-full";
  return "cell-available";
}

// Fallback hours when a week has slots but none carry a start_time (shouldn't happen in practice).
const FALLBACK_HOURS = Array.from({ length: 14 }, (_, i) => String(8 + i).padStart(2, "0") + ":00");

// SVG icons for the view toggle
function GridIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="1" y="1" width="6" height="6" rx="1" />
      <rect x="9" y="1" width="6" height="6" rx="1" />
      <rect x="1" y="9" width="6" height="6" rx="1" />
      <rect x="9" y="9" width="6" height="6" rx="1" />
    </svg>
  );
}

function ListIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <line x1="1" y1="3" x2="15" y2="3" />
      <line x1="1" y1="8" x2="15" y2="8" />
      <line x1="1" y1="13" x2="15" y2="13" />
    </svg>
  );
}

export function Calendar({
  slots,
  weekStart,
  onWeekChange,
  isLoggedIn,
  isAdmin = false,
  isAdminManageMode = false,
  myBookedSlotIds,
  myBookingGroupMap,
  creditBalance = 0,
  onRefetch,
  onCancelled,
  onLoginPrompt,
}: CalendarProps) {
  const [selectedSlot, setSelectedSlot] = useState<SlotAvailability | null>(null);
  const [cancelSlot, setCancelSlot] = useState<SlotAvailability | null>(null);
  const [notification, setNotification] = useState("");
  const [calView, setCalView] = useState<CalendarView>("week");
  const [selectedDay, setSelectedDay] = useState(() => {
    const today = new Date();
    const mon = getMonday(weekStart);
    const diff = Math.floor((today.getTime() - mon.getTime()) / 86400000);
    return diff >= 0 && diff < 7 ? diff : 0;
  });

  const showAdminIndicators = isAdmin || isAdminManageMode;
  const useAdminPopup = isAdminManageMode;
  const isMine = (slotId: number) => myBookedSlotIds?.has(slotId) ?? false;

  const todayStr = toLocalDateStr(new Date());
  const monday = useMemo(() => getMonday(weekStart), [weekStart]);

  // Reset selectedDay when navigating to a different week
  useEffect(() => {
    const today = new Date();
    const diff = Math.floor((today.getTime() - monday.getTime()) / 86400000);
    setSelectedDay(diff >= 0 && diff < 7 ? diff : 0);
  }, [monday]);

  const days = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
  }, [monday]);

  // Collect all unique time slots for the grid header
  const timeSlots = useMemo(() => {
    const times = new Set<string>();
    slots.forEach((s) => {
      if (s.resource_type !== "workshop") {
        times.add(s.start_time.slice(0, 5));
      }
    });
    if (times.size === 0 && slots.length > 0) {
      FALLBACK_HOURS.forEach((h) => times.add(h));
    }
    return Array.from(times).sort();
  }, [slots]);

  // Build lookup: date -> resource_type -> start_time -> slot
  const slotMap = useMemo(() => {
    const map = new Map<string, Map<string, Map<string, SlotAvailability>>>();
    slots.forEach((s) => {
      const dateKey = s.date;
      if (!map.has(dateKey)) map.set(dateKey, new Map());
      const dayMap = map.get(dateKey)!;
      if (!dayMap.has(s.resource_type)) dayMap.set(s.resource_type, new Map());
      dayMap.get(s.resource_type)!.set(s.start_time.slice(0, 5), s);
    });
    return map;
  }, [slots]);

  const handleCellClick = (slot: SlotAvailability | undefined) => {
    if (!slot) return;
    // If this is the user's own booking, open cancel popup
    if (!useAdminPopup && isMine(slot.slot_id) && myBookingGroupMap?.has(slot.slot_id)) {
      setCancelSlot(slot);
      return;
    }
    setSelectedSlot(slot);
  };

  const handleBooked = (summary: string, customerCode?: string) => {
    setSelectedSlot(null);
    let msg = `Rezervácia potvrdená! ${summary}`;
    if (customerCode) {
      msg += ` — Vaše členské číslo: ${customerCode}`;
    }
    setNotification(msg);
    onRefetch();
    setTimeout(() => setNotification(""), 5000);
  };

  const handleAdminAction = () => {
    onRefetch();
  };

  const prevWeek = () => onWeekChange(addDays(monday, -7));
  const nextWeek = () => onWeekChange(addDays(monday, 7));
  const goToday = () => onWeekChange(getMonday(new Date()));

  const weekLabel = `${formatDateShort(monday)} – ${formatDateShort(addDays(monday, 6))}`;

  const selectedDayDate = days[selectedDay];
  const selectedDateKey = selectedDayDate ? toLocalDateStr(selectedDayDate) : "";

  const hasAnySlots = slots.length > 0;

  // ── Cell renderer with admin indicators ──

  const hasClayground = slots.some((s) => s.clayground && s.is_active !== false);

  const renderCellContent = (slot: SlotAvailability) => {
    const isBlocked = slot.is_active === false;

    if (isBlocked && showAdminIndicators) {
      return <span className="cell-avail cell-avail-blocked">blok.</span>;
    }

    return (
      <span className="cell-avail">
        {slot.booked}/{slot.capacity}
        {slot.clayground && <span className="cell-clayground-mark" aria-label="Clayground"></span>}
      </span>
    );
  };

  // ── Shared renderers for day view ──

  const renderDayTabs = () => (
    <div className="day-tabs">
      {days.map((day, idx) => {
        const dateKey = toLocalDateStr(day);
        const isPast = dateKey < todayStr;
        const isToday = dateKey === todayStr;
        return (
          <button
            key={idx}
            className={`day-tab ${idx === selectedDay ? "active" : ""} ${isPast ? "tab-past" : ""} ${isToday ? "tab-today" : ""}`}
            onClick={() => setSelectedDay(idx)}
          >
            <span className="tab-day">{DAY_NAMES[day.getDay()].slice(0, 2)}</span>
            <span className="tab-date">{day.getDate()}</span>
            {isToday && <span className="tab-today-dot"></span>}
          </button>
        );
      })}
    </div>
  );

  const renderDaySlots = () => {
    const dayMap = slotMap.get(selectedDateKey);
    const isPast = selectedDateKey < todayStr;
    if (!dayMap) return <div className="empty">Žiadne termíny pre tento deň.</div>;

    // Hours come from the slots the admin actually generated for this day, not a fixed table.
    const dayTimes = Array.from(
      new Set(
        Array.from(dayMap.entries())
          .filter(([res]) => res !== "workshop")
          .flatMap(([, resMap]) => Array.from(resMap.keys()))
      )
    ).sort();

    return (
      <div className={`day-slots ${isPast ? "day-slots-past" : ""}`}>
        {dayTimes.map((time) =>
          (["stol", "kruh"] as ResourceType[]).map((res) => {
            const resMap = dayMap.get(res);
            const slot = resMap?.get(time);
            if (!slot) return null;
            const isBlocked = slot.is_active === false;
            const colorClass = cellColor(slot, showAdminIndicators);
            const canClick = isAdminManageMode || (!isPast && !isBlocked);

            return (
              <div
                key={`${time}-${res}`}
                className={`day-slot-card res-${res} ${colorClass} ${isMine(slot.slot_id) ? "cell-mine" : ""} ${slot.clayground && !isBlocked ? "cell-clayground" : ""} ${canClick ? "clickable" : ""}`}
                onClick={() => canClick && handleCellClick(slot)}
              >
                <span className="day-slot-time">{time}</span>
                <span className="day-slot-type">
                  {RESOURCE_LABELS[res]}
                  {isBlocked && showAdminIndicators && " (zablokované)"}
                  {slot.clayground && !isBlocked && <span className="day-slot-clayground">Clayground</span>}
                </span>
                {!isBlocked && (
                  <>
                    <span className="day-slot-avail">{slot.booked}/{slot.capacity}</span>
                    <span className="day-slot-price">{(slot.price_cents / 100).toFixed(0)} €</span>
                  </>
                )}
                {isBlocked && showAdminIndicators && (
                  <span className="day-slot-blocked-label">Zablokované</span>
                )}
                {showAdminIndicators && slot.booked > 0 && !isBlocked && (
                  <span className="day-slot-badge">{slot.booked} bookings</span>
                )}
              </div>
            );
          })
        )}

        {/* Workshops */}
        {(() => {
          const wsMap = dayMap.get("workshop");
          if (!wsMap) return null;
          return Array.from(wsMap.values()).map((ws) => {
            const isBlocked = ws.is_active === false;
            const canClick = isAdminManageMode || (!isPast && !isBlocked);
            return (
              <div
                key={ws.slot_id}
                className={`day-slot-card ${isBlocked ? "cell-blocked" : "cell-workshop"} ${isMine(ws.slot_id) ? "cell-mine" : ""} ${canClick ? "clickable" : ""}`}
                onClick={() => canClick && handleCellClick(ws)}
              >
                                <span className="day-slot-time">{ws.start_time.slice(0, 5)}–{ws.end_time.slice(0, 5)}</span>
                <span className="day-slot-type">
                  {ws.title}
                  {isBlocked && showAdminIndicators && " (zablokované)"}
                </span>
                {!isBlocked && (
                  <>
                    <span className="day-slot-avail">{ws.booked}/{ws.capacity}</span>
                    <span className="day-slot-price">{(ws.price_cents / 100).toFixed(0)} €</span>
                  </>
                )}
              </div>
            );
          });
        })()}
      </div>
    );
  };

  // The time header sticks right under the nav bar, so it needs the bar's real height
  // (it wraps on narrow screens). Published as --cal-nav-h on the calendar container.
  const navRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const nav = navRef.current;
    const container = nav?.parentElement;
    if (!nav || !container) return;
    const publish = () => container.style.setProperty("--cal-nav-h", `${nav.offsetHeight}px`);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(nav);
    return () => ro.disconnect();
  }, []);

  return (
    <div className="calendar">
      {/* Navigation */}
      <div className="cal-nav" ref={navRef}>
        <button className="btn btn-icon" onClick={prevWeek}>←</button>
        <button className="btn btn-small" onClick={goToday}>Dnes</button>
        <div className="view-toggle desktop-only">
          <button
            className={`view-toggle-btn ${calView === "week" ? "active" : ""}`}
            onClick={() => setCalView("week")}
            title="Týždenný pohľad"
            aria-label="Week view"
          >
            <GridIcon />
          </button>
          <button
            className={`view-toggle-btn ${calView === "day" ? "active" : ""}`}
            onClick={() => setCalView("day")}
            title="Denný pohľad"
            aria-label="Day view"
          >
            <ListIcon />
          </button>
        </div>
        <span className="cal-week-label">{weekLabel}</span>
        <button className="btn btn-icon" onClick={nextWeek}>→</button>
      </div>

      {notification && <div className="notification">{notification}</div>}

      {!hasAnySlots && (
        <div className="empty-week">Žiadne dostupné termíny pre tento týždeň.</div>
      )}

      {/* Desktop: Week Grid */}
      {hasAnySlots && calView === "week" && (
        <div className="cal-grid desktop-only">
          <table>
            <thead>
              <tr>
                <th className="cal-corner"></th>
                <th className="cal-resource-corner"></th>
                {timeSlots.map((t) => (
                  <th key={t} className="cal-time-header">{t}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {days.map((day) => {
                const dateKey = toLocalDateStr(day);
                const isPast = dateKey < todayStr;
                const isToday = dateKey === todayStr;
                const dayMap = slotMap.get(dateKey);
                const hasWorkshop = dayMap?.has("workshop");
                const rowCount = 2 + (hasWorkshop ? 1 : 0);

                const resources: ResourceType[] = ["stol", "kruh"];
                if (hasWorkshop) resources.push("workshop");

                return resources.map((res, resIdx) => (
                  <tr
                    key={`${dateKey}-${res}`}
                    className={`row-${res} ${isPast ? "row-past" : ""} ${isToday ? "row-today" : ""}`}
                  >
                    {resIdx === 0 ? (
                      <td className="cal-day-label" rowSpan={rowCount}>
                        <div className="day-name">
                          {DAY_NAMES[day.getDay()]}
                        </div>
                        <div className="day-date">{formatDateShort(day)}</div>
                      </td>
                    ) : null}
                    {res === "workshop" ? (
                      (() => {
                        const wsMap = dayMap?.get("workshop");
                        const workshops = wsMap ? Array.from(wsMap.values()) : [];
                        // Workshops may start off the hour (17:15), so they take every hour
                        // column they overlap (lib/workshopGrid).
                        const { starts, covered } = placeWorkshops(workshops, timeSlots);
                        const cells: React.ReactNode[] = [
                          <td key={`${dateKey}-ws-label`} className="cal-resource-label cal-resource-label-ws">Workshop</td>,
                        ];
                        timeSlots.forEach((time) => {
                          const placed = starts.get(time);
                          if (placed) {
                            const { ws, span } = placed;
                            const isBlocked = ws.is_active === false;
                            cells.push(
                              <td
                                key={time}
                                colSpan={span}
                                className={`cal-cell cell-workshop-span ${isMine(ws.slot_id) ? "cell-mine" : ""} ${(!isPast || isAdminManageMode) ? "clickable" : ""}`}
                                onClick={() => (!isPast || isAdminManageMode) && handleCellClick(ws)}
                              >
                                <div className="ws-pill">
                                  <span className="ws-cell-title">
                                    {isBlocked && "Zablokované: "}
                                    {ws.title}
                                  </span>
                                  <span className="ws-cell-avail">{ws.booked}/{ws.capacity}</span>
                                </div>
                              </td>
                            );
                          } else if (!covered.has(time)) {
                            cells.push(
                              <td key={time} className="cal-cell cell-empty"></td>
                            );
                          }
                        });
                        return cells;
                      })()
                    ) : (
                      [
                        <td key={`${dateKey}-${res}-label`} className="cal-resource-label">{RESOURCE_LABELS[res]}</td>,
                        ...timeSlots.map((time) => {
                          const resMap = dayMap?.get(res);
                          const slot = resMap?.get(time);
                          if (!slot) {
                            // No slot for a regular user here (blocked by a workshop, or not
                            // generated) → leave the cell empty. Admins get the real slot with
                            // is_active=false from the admin endpoint and see a blocked cell instead.
                            return <td key={time} className="cal-cell cell-empty"></td>;
                          }
                          const colorClass = cellColor(slot, showAdminIndicators);
                          const isBlocked = slot.is_active === false;
                          const canClick = isAdminManageMode || (!isPast && !isBlocked);
                          return (
                            <td
                              key={time}
                              className={`cal-cell ${colorClass} ${isMine(slot.slot_id) ? "cell-mine" : ""} ${slot.clayground && !isBlocked ? "cell-clayground" : ""} ${canClick ? "clickable" : ""}`}
                              onClick={() => canClick && handleCellClick(slot)}
                            >
                              {renderCellContent(slot)}
                            </td>
                          );
                        }),
                      ]
                    )}
                  </tr>
                ));
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Desktop: Day View */}
      {hasAnySlots && calView === "day" && (
        <div className="cal-day-view desktop-only">
          {renderDayTabs()}
          {renderDaySlots()}
        </div>
      )}

      {/* Mobile: always day view */}
      {hasAnySlots && (
        <div className="cal-mobile mobile-only">
          {renderDayTabs()}
          {renderDaySlots()}
        </div>
      )}

      {/* Legend — Clayground vidí každý, zvyšok len prihlásený (neprihlásenému nič nehovorí) */}
      {(isLoggedIn || hasClayground) && (
        <div className="cal-legend">
          {hasClayground && (
            <span className="legend-item"><span className="legend-dot dot-clayground"></span> Clayground – v štúdiu je prítomná lektorka</span>
          )}
          {isLoggedIn && <span className="legend-item"><span className="legend-dot dot-full"></span> Plné</span>}
          {myBookedSlotIds && myBookedSlotIds.size > 0 && (
            <span className="legend-item"><span className="legend-dot dot-mine"></span> Moja rezervácia</span>
          )}
          {showAdminIndicators && (
            <span className="legend-item"><span className="legend-dot dot-blocked"></span> Zablokované</span>
          )}
        </div>
      )}

      {/* Popup: Admin or Regular */}
      {selectedSlot && useAdminPopup && (
        <AdminSlotPopup
          slot={selectedSlot}
          onClose={() => setSelectedSlot(null)}
          onRefetch={handleAdminAction}
        />
      )}
      {selectedSlot && !useAdminPopup && (
        <BookingPopup
          slot={selectedSlot}
          siblingSlots={
            selectedSlot.resource_type !== "workshop"
              ? Array.from(
                  slotMap.get(selectedSlot.date)?.get(selectedSlot.resource_type)?.values() ?? []
                ).sort((a, b) => a.start_time.localeCompare(b.start_time))
              : []
          }
          isLoggedIn={isLoggedIn}
          creditBalance={creditBalance}
          onClose={() => setSelectedSlot(null)}
          onBooked={handleBooked}
          onLoginPrompt={onLoginPrompt}
        />
      )}

      {cancelSlot && myBookingGroupMap?.has(cancelSlot.slot_id) && (
        <CancelBookingPopup
          slot={cancelSlot}
          bookingGroupId={myBookingGroupMap.get(cancelSlot.slot_id)!}
          onClose={() => setCancelSlot(null)}
          onCancelled={() => {
            setCancelSlot(null);
            onCancelled?.();
          }}
        />
      )}
    </div>
  );
}
